/* --------------------------------------------------------------------- */
/* Semáforo                                                               */
/*                                                                        */
/* Espejo exacto de statusFor() en server/routes/texcumar.js. Si divergen, */
/* el conteo de la cabecera de flota dejaría de cuadrar con el color de    */
/* cada fila, que es el peor defecto posible en un tablero de alarmas.     */
/* --------------------------------------------------------------------- */

export const STATUS_LABEL = {
  ok: 'Óptimo',
  warning: 'Atención',
  critical: 'Crítico',
  sin_datos: 'Sin datos',
  mantenimiento: 'Mantenimiento',
  neutral: 'Sin referencia',
};

/* Orden de gravedad, idéntico al del backend. `mantenimiento` va por encima de
 * `ok` pero por debajo de cualquier aviso: no es un problema, es una ausencia
 * esperada, y no puede competir con lo que sí hay que atender. */
export const STATUS_RANK = { sin_datos: 5, critical: 4, warning: 3, mantenimiento: 2, ok: 1, neutral: 0 };

/* Bandas operativas de respaldo. El servidor envía los umbrales resueltos por
 * serie en cada fila; esto cubre una serie que el catálogo todavía no conoce. */
export const BANDAS_CULTIVO = {
  oxygen: { min: 4, idealMin: 6, idealMax: 9, max: 12 },
  temperature: { min: 25, idealMin: 28.4, idealMax: 28.9, max: 29 },
};

export const BANDAS_REFERENCIA = {
  temperature: { min: 15, idealMin: 27.5, idealMax: 28.5, max: 28.5 },
  salinity: { min: 28, idealMin: 28, idealMax: 38, max: 38 },
};

// Umbrales de una fila: los que envió el servidor, o el respaldo según su tipo.
export function bandasDe(room) {
  if (room?.umbrales) return room.umbrales;
  return room?.tipo === 'referencia' ? BANDAS_REFERENCIA : BANDAS_CULTIVO;
}

/* Una banda es válida si sus cuatro números están en orden no decreciente.
 * Se admite idealMax == max —no siempre existe un ámbar alto— pero no un
 * orden invertido: dejaría tramos del rango sin color definido. */
export function validarBanda(banda) {
  if (!banda) return 'Falta la banda';
  const claves = ['min', 'idealMin', 'idealMax', 'max'];
  const valores = claves.map((k) => Number(banda[k]));
  if (valores.some((v) => !Number.isFinite(v))) return 'Los cuatro valores deben ser números';
  const [min, idealMin, idealMax, max] = valores;
  if (min > idealMin) return 'Mínimo no puede superar al inicio del rango ideal';
  if (idealMin > idealMax) return 'El rango ideal está invertido';
  if (idealMax > max) return 'El fin del rango ideal no puede superar al máximo';
  return null;
}

/* Tramos de color de una banda, para dibujarla.
 *
 * Rojo fuera de [min, max], ámbar entre el límite y el rango ideal, verde
 * dentro. Es la traducción visual exacta de statusFor(): lo que se pinta y lo
 * que se evalúa salen de la misma definición, así que no pueden discrepar. */
export function tramosDeBanda(banda) {
  if (validarBanda(banda)) return [];
  const min = Number(banda.min);
  const idealMin = Number(banda.idealMin);
  const idealMax = Number(banda.idealMax);
  const max = Number(banda.max);

  // Margen a los lados para que los tramos rojos se vean aunque el valor esté
  // justo en el borde. Un 12 % del ancho total, o 1 unidad si la banda es plana.
  const ancho = max - min;
  const margen = ancho > 0 ? ancho * 0.12 : 1;
  const desde = min - margen;
  const hasta = max + margen;
  const total = hasta - desde;

  const tramo = (from, to, status) => ({
    status,
    from,
    to,
    // Porcentajes listos para el ancho en CSS.
    offset: ((from - desde) / total) * 100,
    width: ((to - from) / total) * 100,
  });

  return [
    tramo(desde, min, 'critical'),
    tramo(min, idealMin, 'warning'),
    tramo(idealMin, idealMax, 'ok'),
    tramo(idealMax, max, 'warning'),
    tramo(max, hasta, 'critical'),
  ].filter((t) => t.width > 0);
}

export function statusFor(value, threshold) {
  if (value === null || value === undefined || !threshold) return 'neutral';
  const number = Number(value);
  if (Number.isNaN(number)) return 'neutral';
  if (number < threshold.min || number > threshold.max) return 'critical';
  if (number < threshold.idealMin || number > threshold.idealMax) return 'warning';
  return 'ok';
}

export function worstOf(a, b) {
  return (STATUS_RANK[b] || 0) > (STATUS_RANK[a] || 0) ? b : a;
}

/* Estados que representan un problema. Es lo que filtra «Solo problemas», lo
 * que un jefe de producción necesita ver al abrir la pantalla, y por tanto el
 * criterio de la vista inicial.
 *
 * `sin_datos` va aquí y no es un detalle: una sala muda es el peor caso, no uno
 * menor —STATUS_RANK la pone por encima de `critical`—, porque de una sala en
 * rojo se sabe qué le pasa y de una muda no se sabe nada. La vista inicial
 * llegó a filtrar solo por critical/warning, y entonces el tablero se veía sano
 * por ausencia de datos malos: la leyenda prometía un gris «sin lecturas
 * recientes» imposible de ver, y las salas de Texcumar 5 cuyo Arduino publica
 * ceros desaparecían de la pantalla en vez de reclamar atención. */
export const PROBLEM_STATUSES = ['critical', 'warning', 'sin_datos'];

export function isProblem(status) {
  return PROBLEM_STATUSES.includes(status);
}

export function summarizeBases(rooms) {
  const grouped = new Map();
  (rooms || []).filter((room) => room.tipo !== 'referencia').forEach((room) => {
    const code = room.base_code || String(room.sala || '').split('-')[0];
    if (!code) return;
    if (!grouped.has(code)) grouped.set(code, {
      code, rooms: 0, active: 0, alerts: 0, measurements: 0,
      temperatures: [], oxygenLevels: [], latest_at: null, status: 'neutral',
    });
    const base = grouped.get(code);
    const active = room.status !== 'sin_datos';
    base.rooms += 1;
    base.active += active ? 1 : 0;
    base.alerts += isProblem(room.status) ? 1 : 0;
    base.measurements += Number(room.measurements) || 0;
    base.status = worstOf(base.status, room.status);
    if (active && Number(room.do_measurements) > 0) {
      if (room.current_temperatura_do !== null && room.current_temperatura_do !== undefined
          && Number.isFinite(Number(room.current_temperatura_do))) base.temperatures.push(Number(room.current_temperatura_do));
      if (room.current_oxigeno_mgl !== null && room.current_oxigeno_mgl !== undefined
          && Number.isFinite(Number(room.current_oxigeno_mgl))) base.oxygenLevels.push(Number(room.current_oxigeno_mgl));
    }
    const seen = room.last_seen_at || room.latest_at;
    if (seen && (!base.latest_at || new Date(seen) > new Date(base.latest_at))) base.latest_at = seen;
  });

  const average = (values) => values.length
    ? values.reduce((total, value) => total + value, 0) / values.length
    : null;
  return [...grouped.values()].map((base) => ({
    ...base,
    temperature: average(base.temperatures),
    oxygen: average(base.oxygenLevels),
  })).sort((a, b) => a.code.localeCompare(b.code));
}

export function roomForPresentation(room) {
  if (!room?.respaldo_nodered) return room;
  const bands = bandasDe(room);
  const status = [
    statusFor(room.current_temperatura_do, bands.temperature),
    statusFor(room.current_oxigeno_mgl, bands.oxygen),
    statusFor(room.current_saturacion_pct, bands.saturation),
  ].reduce(worstOf, 'neutral');
  return { ...room, status, stale_level: 'ok', presentacion: true };
}

/* ¿La cabecera debe mostrar la flota en vez de un valor puntual?
 *
 * Lo decide cuántas salas quedan dentro del filtro, NO la base. En cuanto hay
 * más de una, el «valor actual» es la última lectura de una sola de ellas y
 * puede marcar «Óptimo» mientras otra está en hipoxia. Filtrar por base sin
 * elegir sala deja varias salas dentro, así que también es modo flota.
 *
 * Con una sola sala en el alcance, el valor puntual sí la describe y las
 * tarjetas con Mín/Prom/Máx son lo correcto. */
export function isFleetView(roomsEnAlcance) {
  return roomsEnAlcance > 1;
}

/* Cuántas salas de cultivo quedan dentro del filtro actual.
 *
 * Es lo único que decide si la cabecera muestra la flota o un valor puntual.
 * Ha fallado dos veces por mirar el filtro en vez del alcance: primero con la
 * base, y después con la unidad. Filtrar TEX04-002 sin elegir sensor deja DOS
 * salas dentro, así que sigue siendo modo flota. */
export function roomsInScope(rooms, salaFilter, sensorFilter) {
  if (!Array.isArray(rooms)) return 0;
  const sala = !salaFilter || salaFilter === 'all' ? null : String(salaFilter);
  const sensor = !sensorFilter || sensorFilter === 'all' ? null : String(sensorFilter);

  return rooms.filter((room) => {
    if (sala && String(room.sala) !== sala) return false;
    if (sensor && String(room.sensor_num) !== sensor) return false;
    return true;
  }).length;
}

/* Orden por criticidad, no alfabético. Espejo de byCriticality() en el backend,
 * para que reordenar en el cliente no contradiga lo que envía el servidor.
 *
 * El desempate era la saturación ascendente y se cambió por el orden del
 * catálogo: metía en el orden una magnitud que la vista general ya no muestra
 * y, con todas las salas en verde, reordenaba la lista en cada lectura, así
 * que la misma sala nunca estaba dos veces en el mismo sitio.
 *
 * El desempate final usa `serie` y no `sala`: dos salas de cultivo de la misma
 * unidad comparten `sala`, y el orden quedaba indeterminado entre ellas. */
export function byCriticality(a, b) {
  const rank = (STATUS_RANK[b.status] || 0) - (STATUS_RANK[a.status] || 0);
  if (rank !== 0) return rank;
  if (Number.isFinite(a.orden) && Number.isFinite(b.orden) && a.orden !== b.orden) return a.orden - b.orden;
  if (Number.isFinite(a.orden) !== Number.isFinite(b.orden)) return Number.isFinite(a.orden) ? -1 : 1;
  return String(a.serie || a.sala || '').localeCompare(String(b.serie || b.sala || ''));
}
